import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listProgramSignatories, listSignatures, setProgramSignatories, type ProgramSignatory } from "@/api/client";
import { useSession } from "@/auth/session";
import { ErrorBanner, ToolbarButton } from "@/components/ui";

const selectClass = "mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";

/** Who signs each signature slot of the program's template; the version comes from the issue date. */
export function ProgramSignatories({ programId }: { programId: string }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const manage = can("program.manage") && can("signature.read");
  const slots = useQuery({ queryKey: ["program-signatories", programId], queryFn: () => listProgramSignatories(programId) });
  const signatures = useQuery({ queryKey: ["signatures"], queryFn: listSignatures, enabled: manage });
  // Edits made in this visit, by slot; untouched slots keep the saved choice.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (choice: Record<string, string>) => setProgramSignatories(programId, choice),
    onSuccess: (saved) => {
      queryClient.setQueryData(["program-signatories", programId], saved);
      setEdits({});
    },
  });
  const rows = slots.data ?? [];
  const value = (row: ProgramSignatory) => edits[row.slot] ?? row.signature_id ?? "";

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div>
        <h2 className="text-sm font-semibold">Signatories</h2>
        <p className="mt-1 text-xs text-zinc-500">
          Who signs each signature slot of this program&apos;s template. Each certificate uses the version that covers its
          issue date.
        </p>
      </div>
      <ErrorBanner error={slots.error ?? signatures.error ?? save.error} />
      {slots.isSuccess && rows.length === 0 ? (
        <p className="text-sm text-zinc-600">The program&apos;s template has no signature fields.</p>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((row) => (
          <div key={row.slot} className="text-sm">
            {manage ? (
              <label className="block">
                <span className="font-medium text-zinc-700">{row.slot}</span>
                <select
                  value={value(row)}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [row.slot]: e.target.value }))}
                  className={selectClass}
                >
                  <option value="">Not set</option>
                  {(signatures.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.role_title ? `${s.signatory_name} (${s.role_title})` : s.signatory_name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <p>
                <span className="font-medium text-zinc-700">{row.slot}: </span>
                <span className="text-zinc-900">{row.signatory_name ?? "Not set"}</span>
              </p>
            )}
            {value(row) === "" ? (
              <p className="mt-1 text-xs text-amber-700">{row.slot}: not set — batches can&apos;t be approved</p>
            ) : null}
          </div>
        ))}
      </div>
      {manage && rows.length > 0 ? (
        <ToolbarButton
          tone="primary"
          disabled={save.isPending}
          onClick={() => save.mutate(Object.fromEntries(rows.map((row) => [row.slot, value(row)])))}
        >
          {save.isPending ? "Saving..." : "Save signatories"}
        </ToolbarButton>
      ) : null}
    </div>
  );
}
